import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import * as moment from 'moment';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { BasicService } from 'src/app/shared/services/basic.service';

import { DropdownItem } from '../../models/misc.model';
import { TimezoneService } from '../../services/timezone.service';
import { DateTimeSchedule, RangeValidation } from './shared/models';
import { dateTimeSchedulePastValidator, dateTimeScheduleRangeValidator } from './shared/validators';

@Component({
  selector: 'app-date-time-schedule-modal',
  templateUrl: './date-time-schedule-modal.component.html',
  styleUrls: ['./date-time-schedule-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DateTimeScheduleModalComponent implements OnInit {
  @Input() dateTime: string;
  @Input() modalTitleKey: string;
  @Input() confirmBtnTextKey: string;
  @Input() rangeValidation: RangeValidation = null;
  @Output() closeModal: EventEmitter<DateTimeSchedule> = new EventEmitter();

  form = this.formBuilder.group(
    {
      date: [],
      timezone: [this.alcmeonContextService.self.timezone, Validators.required],
      time: this.formBuilder.group({ h: [], m: [] }),
    },
    { validators: dateTimeSchedulePastValidator() }
  );
  rangeErrorMessage = '';
  startHours: string;
  timezoneItems: DropdownItem[];
  timezoneLabel = this.basicService.translate('preferences/timezone');

  @ViewChild('dateTimeScheduleModalTemplateRef', { static: true }) dateTimeScheduleModalTemplateRef: TemplateRef<any>;

  constructor(
    private dialog: MatDialog,
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private formBuilder: FormBuilder,
    private timezoneService: TimezoneService,
    dateAdapter: DateAdapter<any>
  ) {
    dateAdapter.setLocale(alcmeonContextService.lang);

    const allTimezones = this.alcmeonContextService.self.all_timezones;
    this.timezoneItems = this.timezoneService.buildTimezoneDropdownItems(allTimezones);
  }

  ngOnInit(): void {
    this.initForm();

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: ['date-time-schedule', 'medium-size'],
      autoFocus: false,
      data: {
        title: this.basicService.translate(this.modalTitleKey),
        templateRef: this.dateTimeScheduleModalTemplateRef,
        confirmBtnText: this.basicService.translate(this.confirmBtnTextKey),
        cancelBtnText: this.basicService.translate('cancel'),
        disable: () => this.form.invalid,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.closeModal.emit(this.form.value);
      } else {
        this.closeModal.emit();
      }
    });
  }

  private initForm() {
    if (this.rangeValidation) {
      this.form.addValidators(dateTimeScheduleRangeValidator(this.rangeValidation));
      this.initRangeErrorMessage();
    }

    let dateTime: moment.Moment;

    if (this.dateTime) {
      dateTime = this.timezoneService.getMomentWithTimeZone(this.alcmeonContextService.self.timezone, this.dateTime);
    } else {
      // default date time
      dateTime = this.timezoneService.getMomentWithTimeZone(this.alcmeonContextService.self.timezone);
      dateTime.add(1, 'day');
      dateTime.hours(8);
      dateTime.minutes(0);
      dateTime.seconds(0);
    }
    this.form.get('date').setValue(dateTime);
    this.updateTime(dateTime.format('HH:mm'), true);
  }

  initRangeErrorMessage() {
    const min = `${this.rangeValidation.min.amount} ${this.basicService
      .translate(this.rangeValidation.min.unit)
      .toLowerCase()}`;
    const max = `${this.rangeValidation.max.amount} ${this.basicService
      .translate(this.rangeValidation.max.unit)
      .toLowerCase()}`;
    this.rangeErrorMessage = this.basicService.translate('date-time-modal/error/range', { min, max });
  }

  updateTime(value: string, pristine = false) {
    const [hours, minutes] = value.split(':');
    this.form.get('time.h').setValue(+hours);
    this.form.get('time.m').setValue(+minutes);
    this.startHours = `${hours}:${minutes}`;

    // ngx-mat-timepicker-field does not trigger dirty/touched...
    if (!pristine) {
      this.form.get('time.h').markAsDirty();
      this.form.get('time.m').markAsDirty();
    }
  }
}
